import { useQuery } from '@tanstack/react-query';
import authFetch from './fetch';
import { getQueryParams, makeRequest } from './utils';
import { CacheConfig } from './consts';

const LOCATIONS_PATH = '/api/Locations';

export const getLocationsUrl = (params) => `${LOCATIONS_PATH}${getQueryParams(params)}`;
export const getLocations = (params) => makeRequest(authFetch.get(getLocationsUrl(params)));
export const useGetLocations = (params, opts = {}) => useQuery(
  [getLocationsUrl(params)], () => getLocations(params), { ...CacheConfig.forever, ...opts },
);

export const getLocationsLookupUrl = (params) => `/api/Locations/lookup${getQueryParams(params)}`;
export const getLocationsLookup = async (params) => makeRequest(authFetch.get(getLocationsLookupUrl(params)));
export const useGetLocationsLookup = (params, opts = {}) => useQuery([getLocationsLookupUrl(params)], () => getLocationsLookup(params), { ...CacheConfig.forever, ...opts });

export const getLocationsPaginated = (params) => makeRequest(authFetch.get(`${LOCATIONS_PATH}/Grid${getQueryParams(params)}`));

export const getLocation = (id) => makeRequest(authFetch.get(`${LOCATIONS_PATH}/${id}`));

export const deleteLocation = (id) => makeRequest(authFetch.delete(`${LOCATIONS_PATH}/${id}`));

export const restoreLocation = (id) => makeRequest(authFetch.patch(`${LOCATIONS_PATH}/${id}/restore`));

export const putLocation = (location) => makeRequest(authFetch.put(LOCATIONS_PATH, location));

export const getLocationTypes = () => makeRequest(authFetch.get(`${LOCATIONS_PATH}/LocationTypes`));
export const useGetLocationTypes = (opts = {}) => useQuery(['locationTypes'], getLocationTypes, { ...CacheConfig.forever, ...opts });

export const getGwiLocationTypes = () => makeRequest(authFetch.get(`${LOCATIONS_PATH}/GwiLocationTypes`));

export const getAllowedDeliveryTypes = () => makeRequest(authFetch.get(`${LOCATIONS_PATH}/AllowedDeliveryType`));

// Audit
export const getLocationAudits = (id, params) => makeRequest(authFetch.get(`${LOCATIONS_PATH}/${id}/Audits${getQueryParams(params)}`));
export const getLocationAuditProperties = (id) => makeRequest(authFetch.get(`${LOCATIONS_PATH}/${id}/Audits/Properties`));
export const getLocationAuditUsers = (id) => makeRequest(authFetch.get(`${LOCATIONS_PATH}/${id}/Audits/Users`));

export const getCounties = (params) => makeRequest(
  authFetch.get(`${LOCATIONS_PATH}/Counties${getQueryParams(params)}`),
);

// Contacts
export const saveLocationContact = (id, contact) => makeRequest(
  authFetch.put(`${LOCATIONS_PATH}/${id}/Contacts`, contact),
);

export const removeLocationContact = (id, contactId) => makeRequest(
  authFetch.delete(`${LOCATIONS_PATH}/${id}/Contacts/${contactId}`),
);

export const getLocationContacts = (id, params) => makeRequest(
  authFetch.get(`${LOCATIONS_PATH}/${id}/Contacts${getQueryParams(params)}`),
);

export const getLocationWebsites = (id) => makeRequest(
  authFetch.get(`${LOCATIONS_PATH}/${id}/Websites`),
);

export const saveLocationWebsite = (id, website) => makeRequest(
  authFetch.put(`${LOCATIONS_PATH}/${id}/Websites`, website),
);

export const removeLocationWebsite = (id, websiteId) => makeRequest(
  authFetch.delete(`${LOCATIONS_PATH}/${id}/Websites/${websiteId}`),
);

export const getGroundingLocationTypes = () => makeRequest(authFetch.get(`${LOCATIONS_PATH}/GroundingLocationType`));

export const addOrganizationSpecificRequirement = (locationId, requirement) => makeRequest(
  authFetch.put(`${LOCATIONS_PATH}/${locationId}/OrganizationSpecificRequirements`, requirement),
);

export const getOrganizationSpecificRequirements = (id) => makeRequest(
  authFetch.get(`${LOCATIONS_PATH}/${id}/OrganizationSpecificRequirements`),
);

// Aliases
export const getLocationAliases = (locationId, params) => authFetch
  .get(`${LOCATIONS_PATH}/${locationId}/Aliases${getQueryParams(params)}`)
  .catch((error) => {
    console.log(error);
    throw error;
  });

export const saveLocationAlias = (locationId, data) => makeRequest(
  authFetch.put(`${LOCATIONS_PATH}/${locationId}/Aliases`, data),
);

export const deleteLocationAlias = (locationId, aliasId) => makeRequest(
  authFetch.delete(`${LOCATIONS_PATH}/${locationId}/Aliases/${aliasId}`),
);

export const getLocationsLookupForQuoteLane = (quoteLaneId, moveType, stopType, search) => makeRequest(
  authFetch.get(`${LOCATIONS_PATH}/${quoteLaneId}/${moveType}/${stopType}/lookup${getQueryParams(search)}`),
);

export const useLocationsLookup = (search) => {
  return useQuery({
    queryKey: ['locations', 'lookup', search],
    queryFn: () => getLocationsLookup({ search }),
    ...CacheConfig.forever,
    enabled: !!search && search.length >= 2,
  });
};

// For initial data loading when we have specific location IDs
export const useLocationsLookupById = (ids) => {
  return useQuery({
    queryKey: ['locations', 'lookup', { ids }],
    queryFn: () => getLocationsLookup({ ids }),
    ...CacheConfig.never,
    enabled: !!ids && ids.length > 0,
  });
};